import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { platformSettings } from '@/api/platformSettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export type InviteMethod = 'email_invite' | 'manual_password';

function randomTempPassword() {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '').slice(0, 20);
}

export function InviteMethodToggle({ value, onChange, tempPassword, onTempPasswordChange }: {
  value: InviteMethod;
  onChange: (m: InviteMethod) => void;
  tempPassword: string;
  onTempPasswordChange: (p: string) => void;
}) {
  const { t } = useTranslation();
  const me = useCurrentUser();

  const [providerConfigured, setProviderConfigured] = useState<boolean | null>(null);
  useEffect(() => {
    if (!me.is_super_admin) {
      setProviderConfigured(true); // backend resolves provider per-tenant; assume configured
      return;
    }
    let ignore = false;
    platformSettings.getEmail()
      .then((v) => { if (!ignore) setProviderConfigured(v.provider !== null); })
      .catch(() => { if (!ignore) setProviderConfigured(false); });
    return () => { ignore = true; };
  }, [me.is_super_admin]);

  return (
    <div className="space-y-3">
      <Label>{t('inviteMethod.label')}</Label>
      <div className="flex gap-6 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio" name="method" value="email_invite"
            checked={value === 'email_invite'}
            onChange={() => onChange('email_invite')}
            disabled={providerConfigured === false}
          />
          <span>{t('inviteMethod.emailInvite')}{providerConfigured === false && <span className="text-muted-foreground">{t('inviteMethod.emailInviteDisabled')}</span>}</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio" name="method" value="manual_password"
            checked={value === 'manual_password'}
            onChange={() => onChange('manual_password')}
          />
          <span>{t('inviteMethod.manualPassword')}</span>
        </label>
      </div>
      {value === 'manual_password' && (
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="tempPassword">{t('inviteMethod.tempPassword')}</Label>
            <Input id="tempPassword" value={tempPassword} onChange={(e) => onTempPasswordChange(e.target.value)} />
          </div>
          <Button type="button" variant="outline" onClick={() => onTempPasswordChange(randomTempPassword())}>{t('inviteMethod.generate')}</Button>
        </div>
      )}
    </div>
  );
}
